import * as React from 'react'
import { type LucideIcon, ChevronsUpDown, Check } from 'lucide-react'
import { useDebounce } from 'use-debounce'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useSidebar } from '@/components/ui/sidebar'

type Company = {
  id?: string
  name: string
  logo: LucideIcon | string
  plan: string
  subtitle?: string
}

type CompanySwitcherProps = {
  companies?: Company[]
  onCompanySelect?: (company: Company) => void | Promise<void>
  fetchCompanies?: (query?: string) => Promise<Company[]>
  loadInitialCompanies?: boolean
  placeholder?: string
  selectedCompany?: Company | null
}

export function CompanySwitcher({
  companies = [],
  selectedCompany: initialSelectedCompany,
  onCompanySelect,
  fetchCompanies,
  loadInitialCompanies = true,
  placeholder = 'Search companies...',
}: CompanySwitcherProps) {
  const { state, isMobile } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const [selectedCompany, setSelectedCompany] = React.useState<Company | null>(
    initialSelectedCompany || companies[0] || null,
  )
  const [searchQuery, setSearchQuery] = React.useState('')
  const [apiCompanies, setApiCompanies] = React.useState<Company[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(-1)
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!fetchCompanies) return

    const performSearch = async () => {
      setIsLoading(true)
      try {
        const results = await fetchCompanies(debouncedSearchQuery)
        setApiCompanies(results)
      } catch (error) {
        console.error('Failed to fetch companies:', error)
        setApiCompanies([])
      } finally {
        setIsLoading(false)
      }
    }

    if (loadInitialCompanies || debouncedSearchQuery) {
      performSearch()
    }
  }, [debouncedSearchQuery, fetchCompanies, loadInitialCompanies])

  const allCompanies = fetchCompanies ? apiCompanies : companies

  const filteredCompanies = React.useMemo(() => {
    if (fetchCompanies) {
      return allCompanies
    }

    if (!searchQuery) return allCompanies
    return allCompanies.filter(company =>
      company.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [allCompanies, searchQuery, fetchCompanies])

  const handleCompanySelect = async (company: Company) => {
    setSelectedCompany(company)
    setSearchQuery('')
    setSelectedIndex(-1)
    setIsOpen(false)
    await onCompanySelect?.(company)
  }

  const trigger = (
    <Button
      variant="outline"
      className={cn(
        'w-full justify-between',
        isCollapsed && 'w-8 h-8 p-0 justify-center',
      )}
      role="combobox"
      aria-expanded={isOpen}
      aria-label="Select a company"
    >
      <div className={cn('flex items-center gap-2', isCollapsed && 'gap-0')}>
        {selectedCompany && (
          <div className="flex h-6 w-6 items-center justify-center rounded-xl border bg-muted text-sm font-medium overflow-hidden shrink-0">
            {typeof selectedCompany.logo === 'string' ? (
              <img src={selectedCompany.logo} alt={selectedCompany.name} />
            ) : (
              <selectedCompany.logo className="h-4 w-4" />
            )}
          </div>
        )}
        {!isCollapsed && (
          <span className="truncate">
            {selectedCompany?.name || 'Select company'}
          </span>
        )}
      </div>
      {!isCollapsed && (
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      )}
    </Button>
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < filteredCompanies.length - 1 ? prev + 1 : prev,
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && filteredCompanies[selectedIndex]) {
          handleCompanySelect(filteredCompanies[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        break
    }
  }

  const handlePopoverOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setSelectedIndex(-1)
      setSearchQuery('')
    }
  }

  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  React.useEffect(() => {
    setSelectedIndex(-1)
  }, [searchQuery])

  return (
    <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align={isCollapsed ? 'start' : 'start'}
        side={isMobile || isCollapsed ? 'right' : 'bottom'}
      >
        <div>
          <Input
            className="px-4"
            ref={searchInputRef}
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            variant="popover"
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="max-h-64 overflow-y-auto pt-1">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              Searching companies...
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No companies found
            </div>
          ) : (
            filteredCompanies.map((company, index) => (
              <div
                key={company.id || company.name}
                onClick={() => handleCompanySelect(company)}
                className={cn(
                  'flex items-center gap-2 px-1.5 py-1.5 mx-1.5 my-1.5 text-sm cursor-pointer transition-colors rounded-md',
                  'hover:bg-accent',
                  selectedCompany?.name === company.name && 'bg-accent',
                  selectedIndex === index && 'bg-accent',
                )}
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-lg border bg-muted text-sm font-medium overflow-hidden">
                  {typeof company.logo === 'string' ? (
                    <img src={company.logo} alt={company.name} />
                  ) : (
                    <company.logo className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{company.name}</div>
                  {company.subtitle && (
                    <div className="text-xs text-muted-foreground">
                      {company.subtitle}
                    </div>
                  )}
                </div>
                {selectedCompany?.name === company.name && (
                  <Check className="h-4 w-4" />
                )}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
